import { SPONSORED_DAY_VIEW_TYPES } from '@web-platform/shared-universal-constants/src/adverts';
import { FEATURE_DECISION_KEYS } from '@web-platform/shared-universal-constants-config';
import { getFeatureDecision } from '@web-platform/shared-universal-utils-frontend/src/config';
import { isEmptyObject } from '@web-platform/shared-universal-utils-frontend/src/helpers/helpers';
import { combineReducers } from 'redux';
import { createSelector } from 'reselect';

import combinedExploreAdvertsReducer from './combinedExplore';
import combinedResultsAdvertsReducer from './combinedResults';
import { reportMissingDsaLegalNameOnce } from './dsaMissingLegalName';
import advertEntities, * as fromAdvertEntities from './flightsDayView';
import flightsDayViewIds, * as fromFlightsDayViewIds from './flightsDayViewIds';
import homepageAdvertsReducer from './homepage';
import * as fromHomepageAdverts from './homepage';
import { sponsoredAgentReducer } from './sponsoredAgent';
import { sponsoredAirlineV2Reducer } from './sponsoredAirlineV2';
import { sponsoredContentReducer } from './sponsoredContent';

import type { DestinationGuidebook } from '@web-platform/shared-universal-types/src/components/destinationGuidebook';
import type { Creative } from '@web-platform/shared-universal-types/src/redux/acorn/state/flightsDayView/entitiesSearchResults/adverts/types';
import type {
  SponsoredContentState,
  SponsoredDestinationV2HeaderProps,
  GuidebookType,
  SponsoredDestinationTrackingInfoProps,
} from '@web-platform/shared-universal-types/src/redux/state/entities/sponsoredContent';
import type { AdvertsState } from '@web-platform/shared-universal-types/src/redux/state/shared/adverts';
import type { EmptyObject, ID } from '@web-platform/shared-universal-types-utils/src/utils';

const advertsReducer = combineReducers({
  flightsDayView: advertEntities,
  flightsDayViewIds,
  sponsoredAgent: sponsoredAgentReducer,
  sponsoredAirlineV2: sponsoredAirlineV2Reducer,
  combinedResults: combinedResultsAdvertsReducer,
  homepage: homepageAdvertsReducer,
  combinedExplore: combinedExploreAdvertsReducer,
  sponsoredContent: sponsoredContentReducer,
});

export default advertsReducer;

export const getSponsoredAgent = (state: AdvertsState) => state.sponsoredAgent;

export const getSponsoredAirlineV2 = (state: AdvertsState) => {
  const content = state.sponsoredAirlineV2;
  if (!content) return null;

  const hasLegalName = !!content.pioPartnerName;
  if (!hasLegalName) {
    reportMissingDsaLegalNameOnce(content.tracking?.campaignId ?? 'sponsored_airline_v2');
  }

  const shouldCheckDsaCompliance = getFeatureDecision(
    FEATURE_DECISION_KEYS.ADS_DSA_MODAL_UPDATED_WITH_LEGAL_NAME_WEB,
  );

  return shouldCheckDsaCompliance && !hasLegalName ? null : content;
};

export const isSponsoredAirlineV2FilterActive = (state: AdvertsState) =>
  !!state.sponsoredAgent && !!getSponsoredAirlineV2(state);

// flightsDayView
export const getAllFlightsDayViewAdverts = (state: AdvertsState) =>
  fromAdvertEntities.all(state.flightsDayView);

export const getFlightsDayViewAdvert = <C extends Creative>(state: AdvertsState, id: ID) =>
  fromAdvertEntities.get<C>(state.flightsDayView, id);

export const getFlightsDayViewAdvertIds = (state: AdvertsState) =>
  fromFlightsDayViewIds.getAll(state.flightsDayViewIds);

// homepage
export const getHomepageAdverts = (state: AdvertsState) => state.homepage;
export const getNavCardAdvert = (state: AdvertsState, id: string) =>
  fromHomepageAdverts.getNavCardAdvert(state.homepage, id);
export const getAllNavCardAdverts = (state: AdvertsState) =>
  fromHomepageAdverts.getAllNavCardAdverts(state.homepage);
export const getCreativesStatus = (state: AdvertsState) =>
  fromHomepageAdverts.getCreativesStatus(state.homepage);
export const getGraphicPromoAdvert = (state: AdvertsState, id: string) =>
  fromHomepageAdverts.getGraphicPromoAdvert(state.homepage, id);
export const getAllGraphicPromoAdverts = (state: AdvertsState) =>
  fromHomepageAdverts.getAllGraphicPromoAdverts(state.homepage);
export const getHomepageHeroAdvert = (state: AdvertsState, id: string) =>
  fromHomepageAdverts.getHomepageHeroAdvert(state.homepage, id);
export const getAllHomepageHeroAdverts = (state: AdvertsState) =>
  fromHomepageAdverts.getAllHomepageHeroAdverts(state.homepage);
export const getHomepageVideoAdvert = (state: AdvertsState, id: string) =>
  fromHomepageAdverts.getHomepageVideoAdvert(state.homepage, id);
export const getAllHomepageVideoAdverts = (state: AdvertsState) =>
  fromHomepageAdverts.getAllHomepageVideoAdverts(state.homepage);

// sponsoredContent
export const getSponsoredContent = (state: AdvertsState) => state.sponsoredContent;

const GUIDEBOOK_INITIAL_STATE: DestinationGuidebook = {
  header: null,
  needToKnow: [],
  footer: null,
};

const mapGuidebookContent = (state: SponsoredContentState | EmptyObject): DestinationGuidebook => {
  if (!('payload' in state) || !state.payload) {
    return GUIDEBOOK_INITIAL_STATE;
  }

  const { destination, need_to_know: needToKnow } = state.payload;

  const headerProps = destination?.data
    ? { backgroundImageUrl: destination.data.guidebook_image }
    : null;

  const needToKnowProps = needToKnow?.data
    ? needToKnow.data.map((data) => ({
        title: data.title,
        content: data.description,
        link: data.link,
        linkText: data.link_text,
      }))
    : [];

  const footerProps = destination?.data
    ? { partnerName: destination.data.partner_name, logoUrl: destination.data.alt_partner_logo }
    : null;

  return {
    header: headerProps,
    needToKnow: needToKnowProps,
    footer: footerProps,
  };
};

export const getGuidebookContent = (state: AdvertsState): DestinationGuidebook =>
  mapGuidebookContent(state.sponsoredContent);

// sponsoredDestinationsV2
export const sponsoredDestinationV2Enabled = createSelector(
  [getSponsoredContent],
  (sponsoredContent) =>
    !isEmptyObject(sponsoredContent) &&
    sponsoredContent.type === SPONSORED_DAY_VIEW_TYPES.DESTINATION_V2,
);

export const getSponsoredDestinationsV2Data = createSelector(
  [getSponsoredContent],
  (
    sponsoredContent,
  ): {
    sponsoredDestinationHeader: SponsoredDestinationV2HeaderProps | null;
    sponsoredDestinationGuidebook: GuidebookType | null;
    sponsoredDestinationGuidebookEnabled: boolean;
    sponsoredDestinationTrackingInfo: SponsoredDestinationTrackingInfoProps | null;
  } => {
    const header = sponsoredContent.payload?.header?.fields;
    const guidebook = sponsoredContent.payload?.guidebook;
    const guidebookEnabled = sponsoredContent.payload?.guidebookEnabled;
    const trackingInfo = sponsoredContent.payload?.trackingData;

    // Type guard to ensure header has required V2 fields
    const isValidHeader = (h: typeof header): h is SponsoredDestinationV2HeaderProps =>
      h !== null &&
      h !== undefined &&
      'name' in h &&
      'description' in h &&
      'heroImageUrl' in h &&
      'partnerLogo' in h &&
      'partnerName' in h;

    const isValidTrackingInfo = (
      t: typeof trackingInfo,
    ): t is SponsoredDestinationTrackingInfoProps =>
      t !== null && t !== undefined && 'analyticsProperties' in t && 'pixels' in t;

    return {
      sponsoredDestinationHeader: isValidHeader(header) ? header : null,
      sponsoredDestinationGuidebook: guidebook ?? null,
      sponsoredDestinationGuidebookEnabled: !!guidebookEnabled,
      sponsoredDestinationTrackingInfo: isValidTrackingInfo(trackingInfo) ? trackingInfo : null,
    };
  },
);
